import { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Users, PlusCircle, MessageCircle, User, Sparkles } from 'lucide-react';
import { streamAPI } from '../services/api';
import { config, getVideoUrl, getImageUrl } from '../config';
import useDualTrackPlayback from './playback/useDualTrackPlayback';

const clampVolume = (value, fallback = 100) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, Math.min(100, parsed));
};

const clearStateAfterDelay = (setter, key, delayMs) => {
  setTimeout(() => {
    setter(prev => { const n = { ...prev }; delete n[key]; return n; });
  }, delayMs);
};

const MAGIC_CONFETTI_COLORS = ['#a855f7', '#ec4899', '#f59e0b', '#3b82f6', '#10b981'];

const MagicConfettiPiece = ({
  duration,
  delay,
  distanceX,
  distanceY,
  rotate,
  width,
  height,
  borderRadius,
  color,
  onComplete,
}) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, duration + delay);
    return () => clearTimeout(timer);
  }, [delay, duration, onComplete]);

  return (
    <span
      className="magic-confetti-piece"
      style={{
        '--magic-confetti-duration': `${duration}ms`,
        '--magic-confetti-delay': `${delay}ms`,
        '--magic-confetti-x': `${distanceX}px`,
        '--magic-confetti-y': `${distanceY}px`,
        '--magic-confetti-rotate': `${rotate}deg`,
        width: `${width}px`,
        height: `${height}px`,
        borderRadius,
        background: color,
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// VideoPost — 单个直播间卡片
//
// 状态机（三段式）：
//   Intro  → 片头视频播放一次（可选，没有则跳过）
//   Idle   → 待机视频循环播放
//   Action → 互动视频播放一次，结束后回退到 Idle
// ---------------------------------------------------------------------------
const FLAME_DURATION = 600;
const ACTION_SEQUENCE_CATEGORIES = ['interaction', 'public'];

const warmImage = (src) => {
  if (!src || typeof Image === 'undefined') return;
  const image = new Image();
  image.src = src;
};

const warmAudio = (src, { purpose = 'warmup' } = {}) => {
  if (!src || typeof document === 'undefined') return () => {};

  const preloadAudio = document.createElement('audio');
  preloadAudio.src = src;
  preloadAudio.preload = 'auto';
  preloadAudio.style.display = 'none';
  preloadAudio.dataset.preloadKind = 'audio';
  preloadAudio.dataset.preloadSrc = src;
  preloadAudio.dataset.preloadPurpose = purpose;

  const cleanup = () => {
    preloadAudio.removeEventListener('canplay', cleanup);
    preloadAudio.removeEventListener('loadeddata', cleanup);
    preloadAudio.removeEventListener('error', cleanup);
    if (preloadAudio.parentNode) {
      preloadAudio.parentNode.removeChild(preloadAudio);
    }
  };

  preloadAudio.addEventListener('canplay', cleanup, { once: true });
  preloadAudio.addEventListener('loadeddata', cleanup, { once: true });
  preloadAudio.addEventListener('error', cleanup, { once: true });

  document.body.appendChild(preloadAudio);
  preloadAudio.load();
  if (preloadAudio.readyState >= 2) {
    cleanup();
  }

  return cleanup;
};

const PRELOAD_WINDOW_SIZE = 3;
const FEED_ACTIVE_RATIO = 0.5;

const warmVideo = (src, onReady, { purpose = 'warmup' } = {}) => {
  if (!src || typeof document === 'undefined') return () => {};

  const preloadVideo = document.createElement('video');
  preloadVideo.src = src;
  preloadVideo.preload = 'auto';
  preloadVideo.muted = true;
  preloadVideo.playsInline = true;
  preloadVideo.style.display = 'none';
  preloadVideo.dataset.preloadKind = 'video';
  preloadVideo.dataset.preloadSrc = src;
  preloadVideo.dataset.preloadPurpose = purpose;

  const cleanup = () => {
    preloadVideo.removeEventListener('canplay', handleReady);
    preloadVideo.removeEventListener('loadeddata', handleReady);
    preloadVideo.removeEventListener('error', handleReady);
    if (preloadVideo.parentNode) {
      preloadVideo.parentNode.removeChild(preloadVideo);
    }
  };

  const handleReady = () => {
    cleanup();
    onReady();
  };

  preloadVideo.addEventListener('canplay', handleReady, { once: true });
  preloadVideo.addEventListener('loadeddata', handleReady, { once: true });
  preloadVideo.addEventListener('error', handleReady, { once: true });

  document.body.appendChild(preloadVideo);
  preloadVideo.load();
  if (preloadVideo.readyState >= 2) {
    handleReady();
  }

  return cleanup;
};

const setContainerScrollTop = (container, top) => {
  if (!container) return;
  if (typeof container.scrollTo === 'function') {
    container.scrollTo({ top, behavior: 'auto' });
    return;
  }
  container.scrollTop = top;
};

const VideoPost = ({
  stream,
  streamVideos,
  loadStreamVideos,
  generatingState,
  setGeneratingState,
  pollTimers,
  isActive,
  isLoopClone = false,
  interactionEnabled,
  onEntryReady,
}) => {
  const videoRef = useRef(null);
  const pendingSwitchCleanupRef = useRef(null);
  const pendingSwitchTokenRef = useRef(0);
  const actionChainActiveRef = useRef(false);
  const nextActionIndexRef = useRef(0);
  const queuedActionsRef = useRef([]);
  const bgmAudioRef = useRef(null);
  const hasReportedReadyRef = useRef(false);
  const videoErrorRetryCountRef = useRef(0);

  const allVideos = streamVideos[stream.id] || [];
  const hasLoadedStreamVideos = Object.prototype.hasOwnProperty.call(streamVideos, stream.id);
  const introVideo = allVideos.find(v => v.video_category === 'intro');
  const idleVideo = allVideos.find(v => v.video_category === 'idle');
  const orderedActionVideos = useMemo(() => (
    [...allVideos]
      .filter((video) =>
        video.interaction_type
        && video.interaction_label
        && ACTION_SEQUENCE_CATEGORIES.includes(video.video_category)
      )
      .sort((a, b) => {
        const priorityDiff = (b.priority || 0) - (a.priority || 0);
        if (priorityDiff !== 0) return priorityDiff;
        return (a.id || 0) - (b.id || 0);
      })
  ), [allVideos]);
  const fallbackDefaultVideo = hasLoadedStreamVideos ? stream.default_video : null;
  const idleSrc = idleVideo
    ? (idleVideo.video_url || getVideoUrl(idleVideo.filename))
    : (fallbackDefaultVideo?.filename ? (fallbackDefaultVideo.video_url || getVideoUrl(fallbackDefaultVideo.filename)) : null);
  const introSrc = introVideo ? (introVideo.video_url || getVideoUrl(introVideo.filename)) : null;

  // phase: 'pending'(未进入视口) | 'intro' | 'idle' | 'action'
  const [phase, setPhase] = useState('pending');
  const [currentVideoSrc, setCurrentVideoSrc] = useState(null);
  const [currentVideoMeta, setCurrentVideoMeta] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [particles, setParticles] = useState([]);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [showPosterWhileLoading, setShowPosterWhileLoading] = useState(false);
  const [videoVolume, setVideoVolume] = useState(100);
  const [musicVolume, setMusicVolume] = useState(30);
  const [currentMusic, setCurrentMusic] = useState(null);
  const [playbackToken, setPlaybackToken] = useState(0);

  const isLooping = phase === 'idle';

  const getPosterUrl = useCallback((video) => {
    return (
      video?.first_frame_url
      || stream.initial_image_url
      || stream.character_image_url
      || null
    );
  }, [stream.character_image_url, stream.initial_image_url]);

  const pendingPosterUrl = getPosterUrl(
    introVideo
    || idleVideo
    || fallbackDefaultVideo
    || currentVideoMeta
  );
  const shouldShowPosterLayer = showPosterWhileLoading && !isVideoReady;

  useEffect(() => {
    [introVideo, idleVideo, fallbackDefaultVideo].forEach((video) => {
      const poster = getPosterUrl(video);
      if (poster) warmImage(poster);
    });
  }, [fallbackDefaultVideo, getPosterUrl, idleVideo, introVideo]);

  useEffect(() => {
    const poster = getPosterUrl(currentVideoMeta);
    if (poster) warmImage(poster);
  }, [currentVideoMeta, getPosterUrl]);

  useEffect(() => {
    if (!currentVideoMeta) return;
    setVideoVolume(clampVolume(currentVideoMeta.video_volume, 100));
    setMusicVolume(clampVolume(currentVideoMeta.background_music_volume, 30));
  }, [
    currentVideoMeta,
    currentVideoMeta?.id,
    currentVideoMeta?.video_volume,
    currentVideoMeta?.background_music_volume,
  ]);

  useEffect(() => {
    const streamMusicId = stream?.background_music_id ?? currentVideoMeta?.background_music_id;
    const streamMusicUrl = stream?.background_music_audio_url || currentVideoMeta?.background_music_audio_url;
    const bgmEnabled = Number(
      currentVideoMeta?.background_music_enabled
      ?? (streamMusicId ? 1 : 0)
    ) === 1;
    if (bgmEnabled && streamMusicId && streamMusicUrl) {
      setCurrentMusic((prev) => {
        if (
          prev?.id === streamMusicId
          && prev?.audio_url === streamMusicUrl
          && prev?.source === 'stream'
        ) {
          return prev;
        }
        return {
          id: streamMusicId,
          title: stream?.background_music_title || currentVideoMeta?.background_music_title || '背景音乐',
          filename: stream?.background_music_filename || currentVideoMeta?.background_music_filename,
          audio_url: streamMusicUrl,
          source: 'stream',
        };
      });
      return;
    }

    setCurrentMusic(null);
  }, [
    currentVideoMeta?.background_music_audio_url,
    currentVideoMeta?.background_music_enabled,
    currentVideoMeta?.background_music_filename,
    currentVideoMeta?.background_music_id,
    currentVideoMeta?.background_music_title,
    stream?.background_music_audio_url,
    stream?.background_music_filename,
    stream?.background_music_id,
    stream?.background_music_title,
  ]);

  useEffect(() => {
    if (bgmAudioRef.current) {
      bgmAudioRef.current.volume = musicVolume / 100;
    }
  }, [musicVolume]);

  useEffect(() => () => {
    pendingSwitchCleanupRef.current?.();
  }, []);

  useEffect(() => {
    actionChainActiveRef.current = false;
    nextActionIndexRef.current = 0;
    queuedActionsRef.current = [];
  }, [stream.id, orderedActionVideos.map((video) => video.id).join(':')]);

  useEffect(() => {
    if (!isActive || hasLoadedStreamVideos) return;
    void loadStreamVideos(stream.id);
  }, [hasLoadedStreamVideos, isActive, loadStreamVideos, stream.id]);

  const switchToVideo = useCallback((
    nextSrc,
    nextMeta,
    nextPhase,
    {
      deferUntilReady = false,
      restartPlayback = false,
      showPosterOnReadySwitch = false,
    } = {},
  ) => {
    if (!nextSrc) return;

    if (deferUntilReady) {
      pendingSwitchTokenRef.current += 1;
      const token = pendingSwitchTokenRef.current;
      pendingSwitchCleanupRef.current?.();
      pendingSwitchCleanupRef.current = warmVideo(nextSrc, () => {
        if (pendingSwitchTokenRef.current !== token) return;
        pendingSwitchCleanupRef.current = null;
        // 打开 poster，让新视频加载期间显示首帧而非黑屏
        // poster 会在 onPlaying 或 handleDualTrackReady 中关闭
        setShowPosterWhileLoading(true);
        if (showPosterOnReadySwitch) {
          setIsVideoReady(false);
        }
        setCurrentVideoSrc(nextSrc);
        setCurrentVideoMeta(nextMeta);
        setIsMuted(false);
        setPhase(nextPhase);
        if (restartPlayback) {
          setPlaybackToken((value) => value + 1);
        }
      }, { purpose: 'transition' });
      return;
    }

    pendingSwitchTokenRef.current += 1;
    pendingSwitchCleanupRef.current?.();
    pendingSwitchCleanupRef.current = null;
    setCurrentVideoSrc(nextSrc);
    setCurrentVideoMeta(nextMeta);
    setIsMuted(false);
    setPhase(nextPhase);
    if (restartPlayback) {
      setPlaybackToken((value) => value + 1);
    }
  }, []);

  const getNextActionTarget = useCallback((sourceVideos = orderedActionVideos) => {
    if (sourceVideos.length === 0) return null;
    const target = sourceVideos[nextActionIndexRef.current % sourceVideos.length];
    nextActionIndexRef.current = (nextActionIndexRef.current + 1) % sourceVideos.length;
    return target;
  }, [orderedActionVideos]);

  // --- 从头开始播放：intro(有) → idle -----------------------------------------
  const startFreshPlayback = useCallback(() => {
    actionChainActiveRef.current = false;
    queuedActionsRef.current = [];
    videoErrorRetryCountRef.current = 0;
    if (!hasLoadedStreamVideos) {
      setPhase('pending');
      setCurrentVideoSrc(null);
      setCurrentVideoMeta(null);
      setShowPosterWhileLoading(false);
      setIsVideoReady(false);
      setIsMuted(false);
      return;
    }
    if (introSrc) {
      setPhase('intro');
      setCurrentVideoSrc(introSrc);
      setCurrentVideoMeta(introVideo);
    } else if (idleSrc) {
      setPhase('idle');
      setCurrentVideoSrc(idleSrc);
      setCurrentVideoMeta(idleVideo || fallbackDefaultVideo || null);
    }
    setShowPosterWhileLoading(true);
    setIsVideoReady(false);
    setIsMuted(false);
  }, [fallbackDefaultVideo, hasLoadedStreamVideos, idleSrc, idleVideo, introSrc, introVideo]);

  useEffect(() => {
    if (!isActive) {
      pendingSwitchTokenRef.current += 1;
      pendingSwitchCleanupRef.current?.();
      pendingSwitchCleanupRef.current = null;
      // 保留 showPosterWhileLoading 不关闭，让离开的卡片在滑动动画中显示 poster 而非黑屏
      // poster 会在下次 startFreshPlayback 时由 setShowPosterWhileLoading(true) 重新控制
      setPhase('pending');
      hasReportedReadyRef.current = false;
      return;
    }

    startFreshPlayback();
  }, [isActive, startFreshPlayback]);

  // --- 播放源就绪（含仅列表里的 default_video、无视频明细的情况）：视口内且仍待定时补一次开播 ---
  useEffect(() => {
    if (!introSrc && !idleSrc) return;
    if (!isActive || phase !== 'pending') return;
    startFreshPlayback();
  }, [introSrc, idleSrc, isActive, phase, startFreshPlayback]);

  const notifyEntryReady = useCallback(() => {
    if (!hasReportedReadyRef.current && typeof onEntryReady === 'function') {
      hasReportedReadyRef.current = true;
      onEntryReady(stream.id);
    }
  }, [onEntryReady, stream.id]);

  const handleDualTrackReady = useCallback(() => {
    // 不在此处隐藏 poster：视频 ready 不等于正在播放
    // poster 由 onPlaying 事件隐藏，确保移动端 autoplay 失败时 poster 保持可见
    notifyEntryReady();
  }, [notifyEntryReady]);

  const handleDualTrackError = useCallback(() => {
    if (phase === 'intro' && idleSrc) {
      videoErrorRetryCountRef.current = 0;
      setCurrentVideoSrc(idleSrc);
      setCurrentVideoMeta(idleVideo || fallbackDefaultVideo || null);
      setPhase('idle');
      return;
    }
    // idle/action 视频加载失败：重试一次（playbackToken 触发 video.load() 重试）
    if (videoErrorRetryCountRef.current < 1) {
      videoErrorRetryCountRef.current += 1;
      setPlaybackToken((t) => t + 1);
    }
    // 超过重试次数则保持 poster 显示，等待用户刷新
  }, [fallbackDefaultVideo, idleSrc, idleVideo, phase]);

  const {
    isVideoReady: dualTrackReady,
    isAutoplayFallbackMuted,
    attemptPlayback,
    primeAudioFromGesture,
    stopAll,
    restartLoopAlignment,
  } = useDualTrackPlayback({
    videoRef,
    audioRef: bgmAudioRef,
    videoSrc: currentVideoSrc,
    audioSrc: currentMusic?.audio_url || null,
    playbackToken,
    preserveAudioOnPlaybackToken: true,
    active: isActive && Boolean(currentVideoSrc),
    shouldPlay: true,
    muted: isMuted,
    videoVolume,
    audioVolume: musicVolume,
    reuseAudio: true,
    onReady: handleDualTrackReady,
    onPlaybackError: handleDualTrackError,
  });

  const effectiveMuted = isMuted || isAutoplayFallbackMuted;

  useEffect(() => {
    setIsVideoReady(dualTrackReady);
  }, [dualTrackReady]);

  useEffect(() => {
    if (!isActive) {
      stopAll({ resetAudio: true });
    }
  }, [isActive, stopAll]);

  const canInteract = isActive && (
    interactionEnabled
    || hasReportedReadyRef.current
    || isVideoReady
    || dualTrackReady
  );

  // --- 实时生成 -------------------------------------------------------------
  const startRealtimeGeneration = useCallback(async (streamId, interactionType, promptTemplate) => {
    const prompt = promptTemplate || interactionType;

    setGeneratingState(prev => ({
      ...prev,
      [streamId]: { status: 'submitting', progress: 0, message: '正在提交...' }
    }));

    try {
      const formData = new FormData();
      formData.append('interaction_prompt', prompt);

      const res = await fetch(`${config.API_BASE_URL}/streams/${streamId}/add-interaction`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '提交失败');

      const taskId = data.task_id;
      setGeneratingState(prev => ({
        ...prev,
        [streamId]: { taskId, status: 'generating', progress: 10, message: '正在生成视频...' }
      }));

      if (pollTimers.current[streamId]) clearInterval(pollTimers.current[streamId]);

      pollTimers.current[streamId] = setInterval(async () => {
        try {
          const statusRes = await fetch(`${config.API_BASE_URL}/streams/${streamId}/interaction-status/${taskId}`);
          const statusData = await statusRes.json();
          if (!statusRes.ok) return;

          setGeneratingState(prev => ({
            ...prev,
            [streamId]: { taskId, status: statusData.status, progress: statusData.progress, message: statusData.message }
          }));

          if (statusData.status === 'completed' && statusData.video) {
            clearInterval(pollTimers.current[streamId]);
            delete pollTimers.current[streamId];

            actionChainActiveRef.current = true;
            switchToVideo(
              statusData.video.video_url || getVideoUrl(statusData.video.filename, true),
              statusData.video,
              'action',
              { deferUntilReady: true, restartPlayback: true }
            );
            clearStateAfterDelay(setGeneratingState, streamId, 2000);
          }

          if (statusData.status === 'failed') {
            clearInterval(pollTimers.current[streamId]);
            delete pollTimers.current[streamId];
            actionChainActiveRef.current = false;
            clearStateAfterDelay(setGeneratingState, streamId, 3000);
          }
        } catch { /* ignore polling errors */ }
      }, 2500);
    } catch (err) {
      setGeneratingState(prev => ({
        ...prev,
        [streamId]: { status: 'failed', progress: 0, message: '提交失败: ' + err.message }
      }));
      actionChainActiveRef.current = false;
      clearStateAfterDelay(setGeneratingState, streamId, 3000);
    }
  }, [setGeneratingState, pollTimers, switchToVideo]);

  const playQueuedOrFallback = useCallback(() => {
    const queuedAction = queuedActionsRef.current.shift();
    if (queuedAction) {
      actionChainActiveRef.current = true;
      if (queuedAction.generation_mode === 'realtime') {
        startRealtimeGeneration(stream.id, queuedAction.interaction_type, queuedAction.video_prompt);
      } else {
        switchToVideo(
          queuedAction.video_url || getVideoUrl(queuedAction.filename),
          queuedAction,
          'action',
          { deferUntilReady: true, restartPlayback: true }
        );
      }
      return;
    }

    actionChainActiveRef.current = false;
    if (idleSrc) {
      switchToVideo(idleSrc, idleVideo || fallbackDefaultVideo || null, 'idle');
      return;
    }

    if (introSrc) {
      switchToVideo(introSrc, introVideo || null, 'intro');
    }
  }, [
    idleSrc,
    idleVideo,
    introSrc,
    introVideo,
    startRealtimeGeneration,
    fallbackDefaultVideo,
    stream.id,
    switchToVideo,
  ]);

  // --- 视频播完：phase 路由 --------------------------------------------------
  const handleVideoEnd = useCallback(() => {
    if (phase === 'intro' && idleSrc) {
      switchToVideo(
        idleSrc,
        idleVideo || fallbackDefaultVideo || null,
        'idle',
        { deferUntilReady: true, showPosterOnReadySwitch: true }
      );
      return;
    }

    if (phase === 'idle') {
      restartLoopAlignment();
      return;
    }

    if (phase === 'action') {
      playQueuedOrFallback();
    }
  }, [fallbackDefaultVideo, idleSrc, idleVideo, phase, playQueuedOrFallback, restartLoopAlignment, switchToVideo]);

  // --- 一键触发互动 ---------------------------------------------------------
  const handleFireClick = useCallback(async (e) => {
    e.stopPropagation();
    void primeAudioFromGesture();
    void attemptPlayback({ preferSound: true });
    if (!canInteract) return;

    const genState = generatingState[stream.id];
    if (genState && (genState.status === 'generating' || genState.status === 'submitting')) return;

    let actionVideos = orderedActionVideos;
    if (actionVideos.length === 0) {
      const videos = streamVideos[stream.id] || (await loadStreamVideos(stream.id));
      actionVideos = [...(videos || [])]
        .filter((video) =>
          video.interaction_type
          && video.interaction_label
          && ACTION_SEQUENCE_CATEGORIES.includes(video.video_category)
        )
        .sort((a, b) => {
          const priorityDiff = (b.priority || 0) - (a.priority || 0);
          if (priorityDiff !== 0) return priorityDiff;
          return (a.id || 0) - (b.id || 0);
        });
    }
    if (actionVideos.length === 0) return;

    setIsPulsing(true);
    setTimeout(() => setIsPulsing(false), 300);

    const now = Date.now();
    const newParticles = Array.from({ length: 18 }).map((_, i) => {
      const size = 6 + Math.random() * 8;
      const isBar = Math.random() > 0.55;
      const distance = 86 + Math.random() * 138;
      const angle = Math.random() * Math.PI * 2;
      const distanceX = Math.cos(angle) * distance * 1.15;
      const distanceY = (Math.sin(angle) * distance * 0.85) - (52 + Math.random() * 56);

      return {
        id: now + i,
        duration: 500 + Math.random() * 170,
        delay: Math.random() * 40,
        distanceX,
        distanceY,
        rotate: (Math.random() * 420) - 210,
        width: isBar ? size * 1.8 : size,
        height: isBar ? Math.max(4, size * 0.62) : size,
        borderRadius: Math.random() > 0.45 ? '999px' : '3px',
        color: MAGIC_CONFETTI_COLORS[Math.floor(Math.random() * MAGIC_CONFETTI_COLORS.length)],
      };
    });
    setParticles(prev => [...prev, ...newParticles]);

    const target = getNextActionTarget(actionVideos) || actionVideos[0];
    if (!target) return;
    actionChainActiveRef.current = true;

    const particleIds = newParticles.map(p => p.id);
    setTimeout(() => {
      queuedActionsRef.current = [];
      if (target.generation_mode === 'realtime') {
        startRealtimeGeneration(stream.id, target.interaction_type, target.video_prompt);
      } else {
        switchToVideo(
          target.video_url || getVideoUrl(target.filename),
          target,
          'action',
          { deferUntilReady: true, restartPlayback: true }
        );
      }

      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 400);
      setParticles(prev => prev.filter(p => !particleIds.includes(p.id)));
    }, FLAME_DURATION - 50);
  }, [attemptPlayback, canInteract, generatingState, getNextActionTarget, loadStreamVideos, orderedActionVideos, primeAudioFromGesture, startRealtimeGeneration, stream.id, streamVideos, switchToVideo]);

  // --- 渲染 ----------------------------------------------------------------
  const genState = generatingState[stream.id];
  const isGenerating = genState && (genState.status === 'generating' || genState.status === 'submitting');

  const handleVideoSurfaceClick = useCallback((event) => {
    event.stopPropagation();
    void attemptPlayback({ preferSound: true });
  }, [attemptPlayback]);

  const handleEnableSoundClick = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
    void primeAudioFromGesture();
    void attemptPlayback({ preferSound: true });
  }, [attemptPlayback, primeAudioFromGesture]);

  return (
    <div
      className={`stream-mobile-card ${isShaking ? 'screen-shake' : ''}`}
      data-stream-id={stream.id}
      data-active={isActive ? 'true' : 'false'}
      data-loop-clone={isLoopClone ? 'true' : 'false'}
    >
      <div className="stream-mobile-video-wrapper">
        {currentVideoSrc ? (
          <>
        {getPosterUrl(currentVideoMeta) && (
              <img
                className={`video-poster-layer ${shouldShowPosterLayer ? '' : 'is-hidden'}`}
                src={getPosterUrl(currentVideoMeta)}
                alt={`${stream.name} 首帧图`}
              />
            )}
            <video
              ref={videoRef}
              className={`stream-mobile-video ${isVideoReady ? 'is-visible' : ''}`}
              playsInline
              muted={effectiveMuted}
              loop={isLooping}
              preload="auto"
              poster={shouldShowPosterLayer ? (getPosterUrl(currentVideoMeta) || undefined) : undefined}
              onLoadedData={() => setIsVideoReady(true)}
              onCanPlay={() => {}}
              onWaiting={() => {}}
              onPlay={() => {}}
              onPlaying={() => {
                setIsVideoReady(true);
                setShowPosterWhileLoading(false);
                notifyEntryReady();
              }}
              onPause={() => {}}
              onEnded={handleVideoEnd}
              onClick={handleVideoSurfaceClick}
            />
          </>
        ) : (
          <div
            className="stream-mobile-image"
            style={{
              backgroundImage: pendingPosterUrl
                ? `url(${pendingPosterUrl})`
                : (stream.character_image
                  ? `url(${stream.character_image_url || getImageUrl(stream.character_image)})`
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'),
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {Object.prototype.hasOwnProperty.call(streamVideos, stream.id)
              && !introSrc
              && !idleSrc
              && !stream.default_video?.filename
              && phase === 'pending' && (
                <div className="feed-no-video-hint">
                  暂无待机/片头视频
                  <span className="feed-no-video-sub">请在管理页上传片头、待机或默认视频</span>
                </div>
            )}
          </div>
        )}

        {isAutoplayFallbackMuted && isActive && (
          <div className="feed-sound-unlock-hint" role="status">
            <span className="feed-sound-unlock-text">因浏览器限制，当前为静音</span>
            <button
              type="button"
              className="feed-sound-unlock-btn"
              onClick={handleEnableSoundClick}
            >
              开启声音
            </button>
          </div>
        )}

        {particles.map(p => (
          <MagicConfettiPiece
            key={p.id}
            duration={p.duration}
            delay={p.delay}
            distanceX={p.distanceX}
            distanceY={p.distanceY}
            rotate={p.rotate}
            width={p.width}
            height={p.height}
            borderRadius={p.borderRadius}
            color={p.color}
            onComplete={() => setParticles(prev => prev.filter(pt => pt.id !== p.id))}
          />
        ))}

        {genState && (
          <div className="realtime-gen-overlay">
            <div className="realtime-gen-indicator">
              {genState.status === 'failed' ? (
                <span className="gen-status-icon">✗</span>
              ) : genState.status === 'completed' ? (
                <span className="gen-status-icon done">✓</span>
              ) : (
                <div className="gen-spinner" />
              )}
              <span className="gen-status-text">{genState.message}</span>
            </div>
          </div>
        )}

        <div className="feed-bottom-info">
          <div className="feed-author-row">
            <span className="feed-author-name">@{stream.name}</span>
            <button className="feed-follow-btn">Follow</button>
          </div>
          <div className="feed-description">
            {stream.description || '欢迎来到互动空间'}
          </div>
        </div>

        <div className="feed-right-bar">
          <div className="feed-magic-button-shell">
            <button
              type="button"
              className={`feed-flame-icon ${isPulsing ? 'active' : ''} ${isGenerating || !canInteract ? 'disabled' : ''}`}
              onClick={handleFireClick}
              disabled={isGenerating || !canInteract}
              aria-label="Magic"
            >
              <span className="feed-flame-glow-ring" />
              {[0, 1, 2].map((ringIndex) => (
                <span
                  key={ringIndex}
                  className="feed-flame-ripple"
                  style={{ '--ring-delay': `${ringIndex * 0.4}s` }}
                />
              ))}

              <span className="feed-flame-core">
                <span className="feed-flame-glyph">
                  <Sparkles size={32} />
                </span>
                <span className="feed-flame-inner-glow" />
              </span>

            </button>
          </div>
        </div>

        <audio
          ref={bgmAudioRef}
          loop
          preload="auto"
          style={{ display: 'none' }}
          onLoadedMetadata={() => {
            if (bgmAudioRef.current) {
              bgmAudioRef.current.volume = musicVolume / 100;
            }
          }}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// StreamFeed — 主 Feed 页面入口
// ---------------------------------------------------------------------------
const A2HS_SESSION_KEY = 'feed_a2hs_dismissed';

const StreamFeed = () => {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [a2hsVisible, setA2hsVisible] = useState(false);

  const [streamVideos, setStreamVideos] = useState({});
  const streamVideosRef = useRef({});
  const [generatingState, setGeneratingState] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [entryReady, setEntryReady] = useState(false);
  const preloadedStreamIdsRef = useRef(new Set());
  const streamVideoRequestsRef = useRef({});
  const initialWarmupSkippedRef = useRef(false);
  const pollTimers = useRef({});

  const containerRef = useRef(null);
  const isRequesting = useRef(false);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const scrollTopRef = useRef(0);
  const isIOSMobileRef = useRef(false);
  const loopResettingRef = useRef(false);
  const loopScrollTimerRef = useRef(null);
  const loopListKeyRef = useRef('');
  const loopInitTimerRef = useRef(null);

  const loopEnabled = streams.length > 1;
  const loopListKey = useMemo(
    () => streams.map((stream) => stream.id).join(':'),
    [streams]
  );
  const loopedStreams = useMemo(() => {
    if (!loopEnabled) {
      return streams.map((stream, index) => ({
        stream,
        itemKey: `real-${stream.id}-${index}`,
        isLoopClone: false,
      }));
    }

    const firstStream = streams[0];
    const lastStream = streams[streams.length - 1];
    return [
      {
        stream: lastStream,
        itemKey: `loop-before-${lastStream.id}`,
        isLoopClone: true,
      },
      ...streams.map((stream, index) => ({
        stream,
        itemKey: `real-${stream.id}-${index}`,
        isLoopClone: false,
      })),
      {
        stream: firstStream,
        itemKey: `loop-after-${firstStream.id}`,
        isLoopClone: true,
      },
    ];
  }, [loopEnabled, streams]);

  const getWarmupMediaEntries = useCallback((stream, videos) => {
    const allVideos = Array.isArray(videos) ? videos : [];
    const introVideo = allVideos.find((video) => video.video_category === 'intro');
    const idleVideo = allVideos.find((video) => video.video_category === 'idle');
    const actionVideos = allVideos.filter((video) =>
      video.interaction_type
      && video.interaction_label
      && ACTION_SEQUENCE_CATEGORIES.includes(video.video_category)
    );
    const candidates = [introVideo, idleVideo, stream.default_video, ...actionVideos].filter(Boolean);
    const uniqueCandidates = Array.from(new Map(
      candidates.map((video) => [video.id || video.filename || video.video_url, video])
    ).values());

    return {
      posters: uniqueCandidates
        .map((video) => video.first_frame_url || stream.initial_image_url || stream.character_image_url)
        .filter(Boolean),
      videos: uniqueCandidates
        .map((video) => video.video_url || (video.filename ? getVideoUrl(video.filename) : null))
        .filter(Boolean),
      audio: [...new Set([
        stream.background_music_audio_url,
        ...uniqueCandidates.map((video) => video.background_music_audio_url),
      ].filter(Boolean))],
    };
  }, []);

  const jumpToLoopIndex = useCallback((targetIndex) => {
    const container = containerRef.current;
    if (!container) return;
    const itemHeight = container.clientHeight;
    if (!itemHeight) return;

    loopResettingRef.current = true;
    const prevSnapType = container.style.scrollSnapType;
    container.style.scrollSnapType = 'none';
    setContainerScrollTop(container, targetIndex * itemHeight);

    requestAnimationFrame(() => {
      container.style.scrollSnapType = prevSnapType || 'y mandatory';
      requestAnimationFrame(() => {
        loopResettingRef.current = false;
      });
    });
  }, []);

  const normalizeLoopScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || !loopEnabled || loopResettingRef.current) return;

    const itemHeight = container.clientHeight;
    if (!itemHeight) return;

    const currentIndex = Math.round(container.scrollTop / itemHeight);
    if (currentIndex <= 0) {
      jumpToLoopIndex(streams.length);
    } else if (currentIndex >= streams.length + 1) {
      jumpToLoopIndex(1);
    }
  }, [jumpToLoopIndex, loopEnabled, streams.length]);

  const updateActiveIndexFromScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || streams.length === 0) return;
    const itemHeight = container.clientHeight;
    if (!itemHeight) return;

    const rawIndex = Math.round(container.scrollTop / itemHeight);
    if (!loopEnabled) {
      setActiveIndex(Math.max(0, Math.min(streams.length - 1, rawIndex)));
      return;
    }

    if (rawIndex <= 0) {
      setActiveIndex(streams.length - 1);
      return;
    }
    if (rawIndex >= streams.length + 1) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex(rawIndex - 1);
  }, [loopEnabled, streams.length]);

  // 清理轮询定时器
  useEffect(() => () => {
    Object.values(pollTimers.current).forEach(id => clearInterval(id));
    if (loopScrollTimerRef.current) {
      clearTimeout(loopScrollTimerRef.current);
    }
    if (loopInitTimerRef.current) {
      clearTimeout(loopInitTimerRef.current);
    }
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !loopEnabled) {
      loopListKeyRef.current = loopListKey;
      return;
    }
    if (loopListKeyRef.current === loopListKey) return;
    loopListKeyRef.current = loopListKey;
    const alignLoopStart = () => {
      if (!containerRef.current || !loopEnabled) return;
      if (!containerRef.current.clientHeight) {
        loopInitTimerRef.current = setTimeout(alignLoopStart, 16);
        return;
      }
      loopInitTimerRef.current = null;
      jumpToLoopIndex(1);
    };

    alignLoopStart();
  }, [jumpToLoopIndex, loopEnabled, loopListKey]);

  // 沉浸样式：全端基础沉浸 + iOS 手机专用刘海全屏 + PWA 独立窗口
  useEffect(() => {
    const root = document.getElementById('root');
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isMobileViewport = window.matchMedia('(max-width: 1024px)').matches;
    isIOSMobileRef.current = Boolean(isIOS && isMobileViewport);

    const standalone =
      (typeof window.matchMedia !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches) ||
      (typeof navigator !== 'undefined' && navigator.standalone === true);

    document.documentElement.classList.add('feed-immersive');
    document.body.classList.add('feed-immersive');
    root?.classList.add('feed-immersive');
    if (isIOSMobileRef.current) {
      document.documentElement.classList.add('feed-immersive-ios');
      document.body.classList.add('feed-immersive-ios');
      root?.classList.add('feed-immersive-ios');
    }
    if (standalone) {
      document.documentElement.classList.add('feed-standalone');
      document.body.classList.add('feed-standalone');
      root?.classList.add('feed-standalone');
    }
    return () => {
      document.documentElement.classList.remove('feed-immersive');
      document.body.classList.remove('feed-immersive');
      root?.classList.remove('feed-immersive');
      document.documentElement.classList.remove('feed-immersive-ios');
      document.body.classList.remove('feed-immersive-ios');
      root?.classList.remove('feed-immersive-ios');
      document.documentElement.classList.remove('feed-standalone');
      document.body.classList.remove('feed-standalone');
      root?.classList.remove('feed-standalone');
    };
  }, []);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta?.getAttribute('content') ?? '#000000';
    const ua = navigator.userAgent || '';
    const isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    let themeRemoved = false;
    /*
     * iOS Safari 标签页：meta theme-color 会在状态栏下留出一条与 content 同色的顶带，
     * 并把网页排版整体压到带下方 → 黑带 + 时间/信号叠在黑带上。暂时移除该 meta，离开 Feed 时恢复。
     */
    if (meta && isIOS) {
      meta.remove();
      themeRemoved = true;
    } else {
      meta?.setAttribute('content', '#000000');
    }

    const prevScheme = document.documentElement.style.colorScheme;
    document.documentElement.style.colorScheme = 'dark';
    return () => {
      document.documentElement.style.colorScheme = prevScheme;
      if (themeRemoved && meta) {
        document.head.appendChild(meta);
      }
      if (meta) {
        meta.setAttribute('content', prevTheme);
      }
    };
  }, []);

  // iOS Safari：引导「添加到主屏幕」以获得 black-translucent + 全屏（本标签页内系统栏常不透明）
  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (!isIOS || !window.matchMedia('(max-width: 1024px)').matches) return;
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (typeof navigator !== 'undefined' && navigator.standalone === true);
    if (standalone) return;
    try {
      if (sessionStorage.getItem(A2HS_SESSION_KEY) === '1') return;
    } catch (_) {
      /* private mode */
    }
    setA2hsVisible(true);
  }, []);

  const dismissA2hs = useCallback(() => {
    try {
      sessionStorage.setItem(A2HS_SESSION_KEY, '1');
    } catch (_) {
      /* ignore */
    }
    setA2hsVisible(false);
  }, []);

  const a2hsBanner = a2hsVisible ? (
    <div className="feed-a2hs-banner" role="status">
      <p className="feed-a2hs-text">
        在 Safari 点底栏「分享」→「添加到主屏幕」后打开，可获得全屏沉浸体验（状态栏浮在视频上，更接近抖音）。
      </p>
      <div className="feed-a2hs-actions">
        <button type="button" className="feed-a2hs-btn ghost" onClick={dismissA2hs}>
          本标签页内不再提示
        </button>
      </div>
    </div>
  ) : null;

  useEffect(() => {
    streamVideosRef.current = streamVideos;
  }, [streamVideos]);

  const loadStreamVideos = useCallback(async (streamId) => {
    const cached = streamVideosRef.current[streamId];
    if (cached !== undefined) return cached;
    if (streamVideoRequestsRef.current[streamId]) {
      return streamVideoRequestsRef.current[streamId];
    }
    streamVideoRequestsRef.current[streamId] = (async () => {
      const response = await streamAPI.getStreamVideos(streamId, { publicOnly: true });
      const videos = response.data || [];
      setStreamVideos((prev) => {
        if (prev[streamId] !== undefined) return prev;
        return { ...prev, [streamId]: videos };
      });
      return videos;
    })();
    try {
      return await streamVideoRequestsRef.current[streamId];
    } catch {
      setStreamVideos((prev) => {
        if (prev[streamId] !== undefined) return prev;
        return { ...prev, [streamId]: [] };
      });
      return [];
    } finally {
      delete streamVideoRequestsRef.current[streamId];
    }
  }, []);

  const warmupStreamResources = useCallback(async (streamIndex) => {
    if (streams.length === 0) return;
    const normalizedIndex = ((streamIndex % streams.length) + streams.length) % streams.length;
    const stream = streams[normalizedIndex];
    if (!stream) return;
    if (preloadedStreamIdsRef.current.has(stream.id)) return;

    const videos = await loadStreamVideos(stream.id);
    const resources = getWarmupMediaEntries(stream, videos);
    resources.posters.forEach((src) => warmImage(src));
    resources.videos.forEach((src) => warmVideo(src, () => {}, { purpose: 'warmup' }));
    resources.audio.forEach((src) => warmAudio(src, { purpose: 'warmup' }));
    preloadedStreamIdsRef.current.add(stream.id);
  }, [getWarmupMediaEntries, loadStreamVideos, streams]);

  // --- 鼠标拖拽滚动 --------------------------------------------------------
  const handleMouseDown = (e) => {
    if (e.target.closest('button') || e.target.closest('.feed-right-bar') || e.target.closest('.feed-flame-icon')) return;
    isDragging.current = true;
    startY.current = e.pageY - containerRef.current.offsetTop;
    scrollTopRef.current = containerRef.current.scrollTop;
    containerRef.current.style.cursor = 'grabbing';
    containerRef.current.style.scrollSnapType = 'none';
    containerRef.current.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    containerRef.current.scrollTop = scrollTopRef.current - (e.pageY - containerRef.current.offsetTop - startY.current) * 1.5;
  };

  const handleMouseUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const container = containerRef.current;
    container.style.cursor = 'grab';
    container.style.removeProperty('user-select');
    const itemHeight = container.clientHeight;
    if (!itemHeight) return;
    setContainerScrollTop(container, Math.round(container.scrollTop / itemHeight) * itemHeight);
    setTimeout(() => {
      container.style.scrollSnapType = 'y mandatory';
      updateActiveIndexFromScroll();
      normalizeLoopScroll();
    }, 50);
  };

  const handleScroll = () => {
    updateActiveIndexFromScroll();
    if (!loopEnabled || loopResettingRef.current) return;
    if (loopScrollTimerRef.current) {
      clearTimeout(loopScrollTimerRef.current);
    }
    loopScrollTimerRef.current = setTimeout(() => {
      normalizeLoopScroll();
    }, 90);
  };

  // --- 数据获取 -------------------------------------------------------------
  const fetchStreams = useCallback(async () => {
    if (isRequesting.current) return;
    isRequesting.current = true;
    setLoading(true);
    setError(null);
    try {
      const response = await streamAPI.getStreams(true, true);
      if (response?.data) {
        const raw = Array.isArray(response.data) ? response.data : [];
        const allStreams = [...raw].sort(
          (a, b) => (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0)
        );
        // 立即预加载首个互动空间的片头/待机/互动视频，不等待 React 状态更新
        if (allStreams.length > 0) {
          const firstStream = allStreams[0];
          loadStreamVideos(firstStream.id).then((videos) => {
            const resources = getWarmupMediaEntries(firstStream, videos);
            resources.posters.forEach((src) => warmImage(src));
            resources.videos.forEach((src) => warmVideo(src, () => {}, { purpose: 'warmup' }));
            resources.audio.forEach((src) => warmAudio(src, { purpose: 'warmup' }));
            preloadedStreamIdsRef.current.add(firstStream.id);
          });
        }
        setStreams(allStreams);
      } else {
        throw new Error('数据格式错误');
      }
    } catch (err) {
      setError('获取数据失败: ' + err.message);
      setStreams([]);
    } finally {
      setLoading(false);
      isRequesting.current = false;
    }
  }, [loadStreamVideos, getWarmupMediaEntries]);

  // --- Effects --------------------------------------------------------------
  useEffect(() => {
    fetchStreams();
  }, [fetchStreams]);

  useEffect(() => {
    setActiveIndex(0);
    setEntryReady(false);
    preloadedStreamIdsRef.current = new Set();
    initialWarmupSkippedRef.current = false;
  }, [streams]);

  useEffect(() => {
    setEntryReady(false);
  }, [activeIndex]);

  useEffect(() => {
    if (!streams.length) return;
    // 首次进入跳过批量预热，fetchStreams 已单独预加载首个互动空间
    if (!initialWarmupSkippedRef.current) {
      initialWarmupSkippedRef.current = true;
      return;
    }
    const preloadCount = Math.min(PRELOAD_WINDOW_SIZE, streams.length);
    const indexes = Array.from({ length: preloadCount }, (_, offset) => (
      (activeIndex + offset) % streams.length
    ));
    indexes.forEach((index) => {
      void warmupStreamResources(index);
    });
  }, [activeIndex, streams.length, warmupStreamResources]);

  const handleEntryReady = useCallback((streamId) => {
    if (!streams[activeIndex]) return;
    if (streams[activeIndex].id === streamId) {
      setEntryReady(true);
    }
  }, [activeIndex, streams]);

  // --- 渲染 ----------------------------------------------------------------
  if (loading) {
    return (
      <div className="feed-viewport">
        {a2hsBanner}
        <div className="streams-mobile-loading">
          <div className="loading-spinner" />
          <h2>正在加载互动空间...</h2>
          <p>请稍候，我们正在为您准备精彩内容</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feed-viewport">
        {a2hsBanner}
        <div className="streams-mobile-error">
          <div className="error-icon">⚠️</div>
          <h2>加载失败</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchStreams}>🔄 重新加载</button>
        </div>
      </div>
    );
  }

  if (streams.length === 0) {
    return (
      <div className="feed-viewport">
        {a2hsBanner}
        <div className="streams-mobile-empty">
          <div className="empty-icon">🎬</div>
          <h3>还没有互动空间</h3>
          <p>创建您的第一个AI互动空间</p>
          <button className="btn btn-primary" onClick={() => navigate('/create')}>立即创建互动空间</button>
        </div>
      </div>
    );
  }

  return (
    <div className="feed-viewport">
      {a2hsBanner}
      <div
        className="streams-mobile-container"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onScroll={handleScroll}
      >
        {loopedStreams.map(({ stream, itemKey, isLoopClone }) => (
          <VideoPost
            key={itemKey}
            stream={stream}
            streamVideos={streamVideos}
            loadStreamVideos={loadStreamVideos}
            generatingState={generatingState}
            setGeneratingState={setGeneratingState}
            pollTimers={pollTimers}
            isActive={!isLoopClone && streams[activeIndex]?.id === stream.id}
            isLoopClone={isLoopClone}
            interactionEnabled={entryReady && streams[activeIndex]?.id === stream.id}
            onEntryReady={handleEntryReady}
          />
        ))}
      </div>

      <div className="feed-top-tabs">
        <div className="feed-tab">Following</div>
        <div className="feed-tab">For You</div>
      </div>

      <div className="feed-bottom-nav-container">
        <div className="feed-bottom-nav">
          <div className="feed-nav-tab active"><Home size={22} /></div>
          <div className="feed-nav-tab"><Users size={22} /></div>
          <div className="feed-nav-tab feed-go-live-wrapper" onClick={() => navigate('/create')}>
            <div className="feed-go-live-btn"><PlusCircle size={26} color="white" /></div>
          </div>
          <div className="feed-nav-tab"><MessageCircle size={22} /></div>
          <div className="feed-nav-tab" onClick={() => navigate('/grid')}><User size={22} /></div>
        </div>
      </div>

    </div>
  );
};

export default StreamFeed;
